import { toLower } from "lodash";
import { saveAs } from "file-saver";

const EXCEPCIONES = ["de", "y", "del", "la", "el"];

const capitalizarPrimeraLetra = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const obtenerValorMoneda = (monedas, tipoMoneda) => {
  return monedas.find(({ moneda }) => moneda === tipoMoneda).valor || null;
};

/**
 * Convierte un PDF en base64 a Blob y lo descarga
 * @param {string} base64PDF - PDF en formato base64
 * @param {string} nombreArchivo - Nombre del archivo a descargar
 */
const descargarPDFDesdeBase64 = (base64PDF, nombreArchivo, enqueueSnackbar) => {
  try {
    // Convertir base64 a Blob
    const byteCharacters = atob(base64PDF);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    // Descargar usando file-saver
    saveAs(blob, nombreArchivo);
  } catch (error) {
    console.error("Error al convertir y descargar PDF:", error);
    enqueueSnackbar("Error al descargar el PDF", {
      variant: "error",
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
    });
  }
};

/**
 * Descarga el PDF de un DTE por el proxy backend. La API key de OpenFactura
 * vive solo en el backend: aquí solo se envían el tipo y el id interno del
 * documento, y el backend resuelve token, sucursal y credencial.
 * @param {object} params
 * @param {Function} params.trigger - primer elemento de useDescargarPdfDteMutation()
 * @param {string} params.tipo - "plan" | "tarea" | "afecta"
 * @param {string|number} params.id - id interno del documento
 * @param {string} params.nombreArchivo - nombre del archivo a descargar
 * @param {Function} params.enqueueSnackbar - notistack
 */
const descargarPdfDteProxy = async ({
  trigger,
  tipo,
  id,
  nombreArchivo,
  enqueueSnackbar,
}) => {
  try {
    const data = await trigger({ tipo, id }).unwrap();
    if (!data || !data.pdf) {
      throw new Error("Respuesta sin PDF");
    }
    descargarPDFDesdeBase64(data.pdf, nombreArchivo, enqueueSnackbar);
  } catch (error) {
    enqueueSnackbar("Error al descargar el PDF. Intenta nuevamente.", {
      variant: "error",
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
    });
  }
};

const parseDescripcionToWordFormat = (raw) => {
  if (!raw) return "";

  const normTerms = (s) => {
    let text = s
      .replace(/\bprevired\b/gi, "Previred")
      .replace(/\bsii\b/gi, "SII")
      .replace(/\blre\b/gi, "Libro de Remuneraciones Electrónico")
      .replace(/\bcódigo del trabajo\b/gi, "Código del Trabajo")
      .replace(
        /\bley de modernización del trabajo\b/gi,
        "Ley de Modernización del Trabajo",
      )
      .replace(
        /\bservicio de impuestos internos\b/gi,
        "Servicio de Impuestos Internos",
      )
      .replace(/\bvermehren abogados\b/gi, "VERMEHREN ABOGADOS")
      .replace(/\brrhh\b/gi, "RRHH")
      .replace(/\buf\b/gi, "UF");

    return text;
  };

  const formatToProperCase = (text) => {
    // Verificar si el texto está principalmente en mayúsculas
    const uppercaseChars = (text.match(/[A-ZÁÉÍÓÚÑ]/g) || []).length;
    const lowercaseChars = (text.match(/[a-záéíóúñ]/g) || []).length;
    const totalLetters = uppercaseChars + lowercaseChars;

    // Si más del 70% de las letras están en mayúsculas, convertir a formato oración
    if (totalLetters > 0 && uppercaseChars / totalLetters > 0.7) {
      text = text.toLowerCase();
      // Capitalizar primera letra de cada oración después de punto, inicio de línea, o inicio de texto
      text = text.replace(
        /(^|\n|\.\s+)([a-záéíóúñ])/g,
        (match, prefix, letter) => prefix + letter.toUpperCase(),
      );
    }

    return normTerms(text);
  };

  const blocks = raw
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);

  let result = "";

  for (const block of blocks) {
    const lines = block
      .split(/\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    // Detectar títulos
    const firstLine = lines[0];
    const isLikelyHeading =
      ((/^[A-ZÁÉÍÓÚÑ0-9\s.:()/"'-]+$/.test(firstLine) &&
        firstLine.replace(/[:.]/g, "").length <= 50) ||
        /:$/.test(firstLine)) &&
      lines.length === 1;

    if (isLikelyHeading) {
      let text = firstLine.replace(/:\s*$/, "");
      text = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
      text = text.replace("consultoria", "Consultoría");
      result += `${text}\n\n`;
      continue;
    }

    // Listas numeradas
    const allNumbered = lines.every((l) => /^\d+\.\s+/.test(l));
    if (allNumbered) {
      const items = lines.map((l) =>
        formatToProperCase(l.replace(/^\d+\.\s+/, "")),
      );
      result += items.map((item, i) => `${i + 1}. ${item}`).join("\n") + "\n\n";
      continue;
    }

    // Listas con viñetas
    const allBullets = lines.every((l) => /^(-|•|\*)\s+/.test(l));
    if (allBullets) {
      const items = lines.map((l) =>
        formatToProperCase(l.replace(/^(-|•|\*)\s+/, "")),
      );
      result += items.map((item) => `• ${item}`).join("\n") + "\n\n";
      continue;
    }

    // Procesar el bloque completo
    const processedBlock = formatToProperCase(block);
    result += processedBlock + "\n\n";
  }

  return result.trim();
};

const formatoTitulo = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word, index) => {
      if (EXCEPCIONES.includes(word) && index !== 0) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const decodeHtmlEntities = (str, document) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
};

const formatToSentenceCase = (text) => {
  // Primero convertimos todo a minúsculas
  const lowerCase = toLower(text);

  // Dividimos el texto en oraciones (por punto y espacio)
  return lowerCase.replace(/(^\w|\.\s+\w)/g, (letter) => letter.toUpperCase());
};

const limitarDecimalesSinRelleno = (precio, decimales = 6) => {
  // Redondear el precio a los decimales especificados
  const factor = Math.pow(10, decimales);
  const precioRedondeado = Math.round(precio * factor) / factor;

  // Convertir a string para eliminar ceros innecesarios
  const strPrecio = precioRedondeado.toString();

  // Verificar si hay decimales
  if (strPrecio.indexOf(".") !== -1) {
    // Eliminar ceros innecesarios al final de los decimales
    return parseFloat(strPrecio);
  } else {
    // Retornar el número sin cambios si no hay decimales
    return precioRedondeado;
  }
};

const capitalizarPalabras = (texto) => {
  return (
    texto
      ?.toLowerCase()
      .replace(/(?:^|\s)\S/g, function (letra) {
        return letra.toUpperCase();
      })
      .replace(/\b([EIRLSA]\.)\w*\b/g, function (abreviatura) {
        return abreviatura.toUpperCase();
      }) || "No definido"
  );
};

// Elimina acentos y caracteres especiales
const limpiarString = (texto) => {
  return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

// Elimina espacios innecesarios
const normalizarEspacios = (texto) => {
  return texto.trim().split(/\s+/).join(" ");
};

const normalize = (str) => {
  return str
    ?.toLowerCase()
    .normalize("NFD") // separa letras de los acentos
    .replace(/[\u0300-\u036f]/g, "") // elimina los acentos
    .replace(/\s+/g, " ") // colapsa espacios múltiples
    .trim();
};

//  Elimina espacios, guiones y convierte a minúsculas
const normalizeString = (str) => {
  return str.trim().toLowerCase().replace(/[\s-]/g, "");
};

const isEmptyString = (str) => {
  return str.trim().length === 0;
};

const obtenerNombreCorto = (nombreCompleto) => {
  if (!nombreCompleto) return "";

  const partes = nombreCompleto.trim().split(/\s+/);

  if (partes.length === 1) return capitalizarPalabras(partes[0]);

  if (partes.length === 2) return capitalizarPalabras(partes.join(" "));

  const [primerNombre, ...resto] = partes;
  const primerApellido = resto.length ? resto[0] : "";

  return capitalizarPalabras(`${primerNombre} ${primerApellido}`);
};

const formatRutManual = (rut) => {
  // Elimina todos los caracteres que no sean números o la letra 'k'
  const cleanedRut = rut.replace(/[^0-9kK]/g, "");

  // Separa el dígito verificador del resto del RUT
  const dv = cleanedRut.slice(-1).toUpperCase(); // Dígito verificador (puede ser 'K')
  const body = cleanedRut.slice(0, -1); // Parte numérica del RUT

  // Formatea la parte numérica con puntos
  const formattedBody = body
    .split("")
    .reverse()
    .join("")
    .replace(/(\d{3})/g, "$1.")
    .split("")
    .reverse()
    .join("")
    .replace(/^\./, "");

  // Retorna el RUT formateado
  return `${formattedBody}-${dv}`;
};

export {
  capitalizarPrimeraLetra,
  capitalizarPalabras,
  limpiarString,
  isEmptyString,
  obtenerValorMoneda,
  limitarDecimalesSinRelleno,
  decodeHtmlEntities,
  normalizarEspacios,
  normalizeString,
  formatToSentenceCase,
  formatRutManual,
  obtenerNombreCorto,
  formatoTitulo,
  parseDescripcionToWordFormat,
  normalize,
  descargarPDFDesdeBase64,
  descargarPdfDteProxy,
};
